/**
 * Iter XX.272 — Gully Challenge #001 · Detail page.
 *
 * Single component covering all four states:
 *   · not_joined (free)     → JOIN THE CHALLENGE 🔒 → beginPlusPurchase
 *   · invited (complimentary) → YOU'RE INVITED → direct join
 *   · participating          → DAY N/7 + focus + Share progress
 *   · completed              → evidence-supported summary
 *
 * Never fabricates progress or improvement (XX.271 evidence rule).
 * Never invents participants, testimonials, or scarcity.
 * Never modifies RevenueCat / Stripe / entitlements.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, Trophy, Share2 } from "lucide-react";
import PageErrorBoundary from "../components/PageErrorBoundary";
import PrimarySurfaceNav from "../components/PrimarySurfaceNav";
import {
  fetchCurrentChallenge,
  joinChallenge,
  setChallengeFocus,
  shareChallengeProgress,
  fetchChallengeFeed,
  stashChallengeOrigin,
} from "../lib/challenge";
import { trackFunnel } from "../lib/activationFunnel";
import { beginPlusPurchase } from "../lib/beginPlusPurchase";
import { hapticLight } from "../lib/haptics";
import { toast } from "sonner";

const FOCUS_OPTIONS = [
  { id: "batting",  label: "Batting" },
  { id: "bowling",  label: "Bowling" },
  { id: "fielding", label: "Fielding" },
  { id: "mindset",  label: "Confidence / mindset" },
  { id: "other",    label: "Something else" },
];

export default function ChallengeDetail() {
  return (
    <PageErrorBoundary pageName="Challenge">
      <ChallengeDetailInner />
    </PageErrorBoundary>
  );
}

function ChallengeDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justJoined = params.get("joined") === "1";

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState([]);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    const s = await fetchCurrentChallenge();
    setState(s);
    setLoading(false);
    if (s?.available) {
      const items = await fetchChallengeFeed(s.challenge_id, 20);
      setFeed(items);
    }
  };

  useEffect(() => { reload(); }, []);

  useEffect(() => {
    if (state?.available) {
      try {
        trackFunnel("challenge_detail_view", {
          challenge_id: state.challenge_id,
          user_status:  state.user_status,
          source:       justJoined ? "post_purchase_return" : "direct",
        });
      } catch { /* noop */ }
      if (justJoined) {
        try {
          trackFunnel("challenge_plus_purchase_success", {
            challenge_id: state.challenge_id,
          });
        } catch { /* noop */ }
      }
    }
  }, [state?.available, state?.user_status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6">
        <div className="text-sm text-neutral-500">Loading…</div>
      </div>
    );
  }
  if (!state?.available) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6">
        <Link to="/pulse" className="inline-flex items-center gap-1 text-sm text-neutral-400 mb-6">
          <ArrowLeft size={14} /> Back to Pulse
        </Link>
        <div className="text-neutral-400" data-testid="challenge-unavailable">
          This challenge isn't available yet.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-mobile-nav" data-testid="challenge-content-wrapper">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-neutral-400 mb-4 hover:text-neutral-200"
          data-testid="challenge-back"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <Header state={state} />

        <StateSpecificBody
          state={state}
          setBusy={setBusy}
          busy={busy}
          reload={reload}
        />

        <PublicActivity feed={feed} />
      </div>
      <div className="sm:hidden">
        <PrimarySurfaceNav active="pulse" />
      </div>
    </div>
  );
}

function Header({ state }) {
  const count = state.participant_count || 0;
  return (
    <div className="mb-6" data-testid="challenge-header">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-700/50 flex items-center justify-center">
          <Trophy size={16} className="text-emerald-300" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-400/90 font-semibold">
          Gully Challenge #001
        </div>
      </div>
      <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-50 leading-tight mb-2">
        {state.headline || "7 DAYS. ONE THING."}
      </h1>
      <p className="text-sm text-neutral-300 leading-relaxed">
        {state.description}
      </p>
      <div className="mt-4 text-xs text-neutral-400" data-testid="challenge-participant-count">
        {count === 0
          ? "Be one of the first cricketers to join."
          : `${count} ${count === 1 ? "cricketer" : "cricketers"} joined`}
      </div>
    </div>
  );
}

function StateSpecificBody({ state, setBusy, busy, reload }) {
  const status = state.user_status;
  if (status === "not_joined") {
    return <NotJoinedGate state={state} setBusy={setBusy} busy={busy} />;
  }
  if (status === "invited") {
    return <InvitedCta state={state} setBusy={setBusy} busy={busy} reload={reload} />;
  }
  if (status === "participating") {
    return <ParticipatingBody state={state} setBusy={setBusy} busy={busy} reload={reload} />;
  }
  if (status === "completed") {
    return <CompletedSummary state={state} />;
  }
  return null;
}

// ---- NOT JOINED (free) ------------------------------------------------

function NotJoinedGate({ state, setBusy, busy }) {
  const onJoin = async () => {
    if (busy) return;
    setBusy(true);
    hapticLight();
    try {
      trackFunnel("challenge_join_tap", {
        challenge_id: state.challenge_id,
        source: "detail_page",
      });
    } catch { /* noop */ }
    // 402 path — need Plus. Stash challenge origin then delegate to
    // the existing Plus purchase flow. No new checkout / entitlement
    // logic here.
    const r = await joinChallenge(state.challenge_id);
    if (r.ok) {
      // A previously-Plus user with no participant row — server allowed
      // the join without a paywall. Refresh state and route to focus
      // selection via a location reload.
      window.location.reload();
      return;
    }
    if (r.requires_plus) {
      try {
        trackFunnel("challenge_plus_paywall_view", {
          challenge_id: state.challenge_id,
        });
      } catch { /* noop */ }
      stashChallengeOrigin(state.challenge_id);
      await beginPlusPurchase({ entryPoint: `challenge_${state.challenge_id}` });
      setBusy(false);
      return;
    }
    setBusy(false);
    toast.error("Couldn't start join — try again.");
  };
  return (
    <div className="mt-2" data-testid="challenge-gate-not-joined">
      <button
        onClick={onJoin}
        disabled={busy}
        data-testid="challenge-join-plus-cta"
        className={
          "w-full rounded-2xl px-5 py-4 text-left "
          + "bg-emerald-600/15 border border-emerald-500/50 "
          + "hover:bg-emerald-600/25 transition-colors "
          + "disabled:opacity-50 disabled:cursor-not-allowed"
        }
      >
        <div className="flex items-center justify-between">
          <div className="text-[15px] font-semibold text-emerald-200">
            Join the challenge with Gully Plus
          </div>
          <Lock size={16} className="text-emerald-300" aria-hidden />
        </div>
        <div className="text-xs text-neutral-400 mt-1 leading-relaxed">
          Work on one thing for 7 days and let Gully follow what happens.
        </div>
      </button>
    </div>
  );
}

// ---- INVITED (complimentary) ------------------------------------------

function InvitedCta({ state, setBusy, busy, reload }) {
  const onJoin = async () => {
    if (busy) return;
    setBusy(true);
    hapticLight();
    try {
      trackFunnel("challenge_join_tap", {
        challenge_id: state.challenge_id,
        source: "invited_cta",
      });
    } catch { /* noop */ }
    const r = await joinChallenge(state.challenge_id);
    setBusy(false);
    if (r.ok) {
      try {
        trackFunnel("challenge_joined", {
          challenge_id: state.challenge_id,
          access_type:  "complimentary",
        });
      } catch { /* noop */ }
      reload();
    } else {
      toast.error("Couldn't join — try again.");
    }
  };
  return (
    <div className="mt-2" data-testid="challenge-gate-invited">
      <div className="rounded-2xl px-5 py-4 bg-emerald-950/40 border border-emerald-800/50">
        <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-400/90 font-semibold mb-2">
          You're invited
        </div>
        <p className="text-sm text-neutral-300 leading-relaxed mb-4">
          We're inviting a small group of active Gully cricketers to the first
          Gully Challenge. Your entry is complimentary.
        </p>
        <button
          onClick={onJoin}
          disabled={busy}
          data-testid="challenge-complimentary-join-cta"
          className={
            "inline-flex items-center gap-2 px-4 py-2 rounded-full "
            + "bg-emerald-500 text-neutral-950 text-sm font-medium "
            + "hover:bg-emerald-400 transition-colors disabled:opacity-50"
          }
        >
          Join the challenge <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ---- PARTICIPATING ----------------------------------------------------

function ParticipatingBody({ state, setBusy, busy, reload }) {
  const [area, setArea] = useState(state.user_focus_area || null);
  const [text, setText] = useState(state.user_focus || "");
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const dayLabel = useMemo(() => {
    const d = state.days_elapsed;
    if (!d || d <= 0) return null;
    return `DAY ${d}/${state.total_days || 7}`;
  }, [state.days_elapsed, state.total_days]);

  const hasFocus = Boolean(state.user_focus && state.user_focus.trim());

  const onSaveFocus = async () => {
    if (!area || (text || "").trim().length < 3) {
      toast.error("Add a concrete focus in a short sentence.");
      return;
    }
    setSaving(true);
    const r = await setChallengeFocus(state.challenge_id, {
      focusArea: area, focusText: text,
    });
    setSaving(false);
    if (r.ok) {
      try {
        trackFunnel("challenge_focus_created", {
          challenge_id: state.challenge_id,
          focus_area:   area,
        });
      } catch { /* noop */ }
      reload();
    } else {
      toast.error("Couldn't save focus.");
    }
  };

  const onShareProgress = async () => {
    setSharing(true);
    const day = state.days_elapsed || 1;
    const title = `Gully Challenge · Day ${day}/${state.total_days || 7}`;
    const body = state.user_focus
      ? `Working on: ${state.user_focus}`
      : "Working on my one thing.";
    const r = await shareChallengeProgress(state.challenge_id, { title, body });
    setSharing(false);
    if (r.ok) {
      try {
        trackFunnel("challenge_progress_share", {
          challenge_id: state.challenge_id,
          challenge_day: day,
        });
      } catch { /* noop */ }
      toast.success("Shared to Pulse.");
      reload();
    } else {
      toast.error("Couldn't share — try again.");
    }
  };

  return (
    <div className="mt-2 space-y-5" data-testid="challenge-participating">
      {dayLabel && (
        <div className="rounded-2xl px-5 py-4 bg-neutral-900 border border-neutral-800">
          <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-400/90 font-semibold mb-1">
            {dayLabel}
          </div>
          {hasFocus ? (
            <div className="text-[15px] text-neutral-50 leading-snug">
              Current focus:{" "}
              <span className="text-neutral-100 font-medium">
                {state.user_focus}
              </span>
            </div>
          ) : (
            <div className="text-sm text-neutral-400">
              Pick your one thing below.
            </div>
          )}
        </div>
      )}

      {!hasFocus && (
        <div className="rounded-2xl px-5 py-4 bg-neutral-900 border border-neutral-800">
          <div className="text-sm text-neutral-100 font-medium mb-3">
            What ONE thing do you want to improve over the next 7 days?
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {FOCUS_OPTIONS.map((o) => (
              <button
                key={o.id}
                onClick={() => setArea(o.id)}
                data-testid={`challenge-focus-option-${o.id}`}
                className={
                  "text-xs px-3 py-1.5 rounded-full border transition-colors "
                  + (area === o.id
                    ? "border-emerald-500 text-emerald-200 bg-emerald-500/10"
                    : "border-neutral-700 text-neutral-300 hover:border-neutral-500")
                }
              >
                {o.label}
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="One concrete thing — e.g. Stay balanced when driving through cover."
            data-testid="challenge-focus-text"
            className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-600 mb-3"
            rows={2}
          />
          <button
            onClick={onSaveFocus}
            disabled={saving || !area || (text || "").trim().length < 3}
            data-testid="challenge-focus-save"
            className={
              "inline-flex items-center gap-1.5 px-4 py-2 rounded-full "
              + "bg-emerald-500 text-neutral-950 text-sm font-medium "
              + "hover:bg-emerald-400 transition-colors disabled:opacity-50"
            }
          >
            Save my focus <ArrowRight size={14} />
          </button>
        </div>
      )}

      {hasFocus && (
        <button
          onClick={onShareProgress}
          disabled={sharing}
          data-testid="challenge-share-progress"
          className={
            "w-full text-left rounded-2xl px-5 py-4 "
            + "bg-neutral-900 border border-neutral-800 "
            + "hover:border-emerald-700/60 transition-colors "
            + "disabled:opacity-50 flex items-center gap-3"
          }
        >
          <Share2 size={18} className="text-emerald-300 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-neutral-100">
              Share progress to Pulse
            </div>
            <div className="text-xs text-neutral-500 mt-0.5">
              You approve the public summary before posting. Private
              chats, videos and coaching stay private.
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

// ---- COMPLETED --------------------------------------------------------

function CompletedSummary({ state }) {
  const hasEvidence = (state.user_activity_count || 0) > 0;
  return (
    <div className="mt-2" data-testid="challenge-completed">
      <div className="rounded-2xl px-5 py-4 bg-emerald-950/40 border border-emerald-800/50">
        <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-400/90 font-semibold mb-1">
          Challenge complete
        </div>
        <div className="text-[15px] text-neutral-50 mb-2">
          7 days working on your cricket.
        </div>
        {state.user_focus && (
          <div className="text-sm text-neutral-300 leading-relaxed">
            You started working on:{" "}
            <span className="text-neutral-100">{state.user_focus}</span>.
          </div>
        )}
        {!hasEvidence && (
          <p className="text-sm text-neutral-400 leading-relaxed mt-3">
            You worked on this for 7 days. Gully doesn't have enough
            comparable evidence yet to say how much it changed.
          </p>
        )}
      </div>
    </div>
  );
}

function PublicActivity({ feed }) {
  if (!feed || feed.length === 0) return null;
  return (
    <div className="mt-8" data-testid="challenge-public-activity">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
        In the challenge
      </div>
      <div className="space-y-3">
        {feed.map((m) => (
          <div
            key={m.moment_id}
            className="rounded-2xl bg-neutral-900 border border-neutral-800 px-4 py-3"
            data-testid="challenge-feed-item"
          >
            <div className="text-[13px] text-neutral-100 font-medium">
              {m.title}
            </div>
            {m.body && (
              <div className="text-xs text-neutral-400 mt-1 leading-relaxed">
                {m.body}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
